import React, { useState, useRef, useMemo } from 'react';
import { SubmissionStatus } from './types';
import type { OtpState, PageData, VerifiedState } from './types';
import { LogoImage } from '../account-creation/pages/LogoImage';
import { checkApplicationStatus, requestOtp, submitApplication, verifyOtp } from './api';
import type { ApplicationFormData } from './api';
import { OTHER_SPORT, REFERRER_MAX_LENGTH } from '../../constants';
import { COUNTRY_OPTIONS, getCityOptions, getStateOptions } from '../../shared/locations';
import { SiteNav } from '../../components/SiteNav/SiteNav';
import { SiteFooter } from '../../components/SiteFooter/SiteFooter';
import { Button } from '../../components/Button/Button';
import arrowIcon from '../../components/brand/icon-arrow-up-right.svg';

// Strips every non-digit character (used to sanitize OTP input + paste).
const NON_DIGIT_RE = /\D/g;

enum Step {
    Email = 'email',
    Otp = 'otp',
    Details = 'details',
    Submitted = 'submitted',
    Pending = 'pending',
    CanReapply = 'can_reapply',
    ApprovedPendingActivation = 'approved_pending_activation',
    AlreadyActivated = 'already_activated',
}

function emptyForm(): ApplicationFormData {
    return {
        first_name: '',
        last_name: '',
        insta_handle: '',
        tiktok_handle: '',
        primary_sport: '',
        athlete_level: '',
        city: '',
        state: '',
        country: '',
        referrer: '',
        heard_about_source: '',
        referral_code: null,
        extra_data: {},
    };
}

export function App({ csrfToken, applicationId, defaultImage, image, applicationError, formConfig, referral_code, backgroundImage, supportedActivities = [], heardAboutOptions }: PageData) {
    const [step, setStep] = useState<Step>(Step.Email);
    const [email, setEmail] = useState('');
    const [emailInput, setEmailInput] = useState('');
    const [emailError, setEmailError] = useState<string | null>(null);
    const [emailLoading, setEmailLoading] = useState(false);

    const [otpState, setOtpState] = useState<OtpState | null>(null);
    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const otpCode = otpDigits.join('');
    const [otpError, setOtpError] = useState<string | null>(null);
    const [otpLoading, setOtpLoading] = useState(false);
    const [verifiedState, setVerifiedState] = useState<VerifiedState | null>(null);

    const [form, setForm] = useState<ApplicationFormData>({
        ...emptyForm(),
        referral_code: referral_code ?? null,
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isReapply, setIsReapply] = useState(false);

    // Track iso codes locally — we store human-readable names in ApplicationFormData
    const [countryIso, setCountryIso] = useState('');
    const [stateIso, setStateIso] = useState('');
    const stateOptions = useMemo(() => countryIso ? getStateOptions(countryIso) : [], [countryIso]);
    const cityOptions = useMemo(() => stateIso ? getCityOptions(countryIso, stateIso) : [], [countryIso, stateIso]);
    const heardAboutFollowUp = heardAboutOptions.find(opt => opt.value === form.heard_about_source)?.followUp;

    function setField(field: keyof ApplicationFormData, value: string) {
        setForm(prev => ({ ...prev, [field]: value }));
    }

    function handleHeardAboutChange(value: string) {
        setForm(prev => ({
            ...prev,
            heard_about_source: value,
            referrer: '',
        }));
    }

    function handleCountryChange(isoCode: string) {
        const country = COUNTRY_OPTIONS.find(c => c.isoCode === isoCode);
        setCountryIso(isoCode);
        setStateIso('');
        setForm(prev => ({ ...prev, country: country?.label ?? '', state: '', city: '' }));
    }

    function handleStateChange(isoCode: string) {
        const state = stateOptions.find(s => s.isoCode === isoCode);
        setStateIso(isoCode);
        setForm(prev => ({ ...prev, state: state?.label ?? '', city: '' }));
    }

    function setExtraField(key: string, values: string[]) {
        setForm(prev => ({ ...prev, extra_data: { ...prev.extra_data, [key]: values } }));
    }

    function toggleExtraFieldOption(key: string, option: string) {
        setForm(prev => {
            const current = prev.extra_data[key] ?? [];
            const next = current.includes(option)
                ? current.filter(v => v !== option)
                : [...current, option];
            return { ...prev, extra_data: { ...prev.extra_data, [key]: next } };
        });
    }

    async function handleEmailSubmit(e: React.FormEvent) {
        e.preventDefault();
        setEmailLoading(true);
        setEmailError(null);
        try {
            const state = await requestOtp(csrfToken, emailInput.trim());
            setEmail(emailInput.trim());
            setOtpState(state);
            setStep(Step.Otp);
        } catch (err) {
            setEmailError(err instanceof Error ? err.message : 'Something went wrong');
        } finally {
            setEmailLoading(false);
        }
    }

    function handleOtpDigitChange(index: number, value: string) {
        const cleaned = value.replace(NON_DIGIT_RE, '').slice(-1);
        const next = [...otpDigits];
        next[index] = cleaned;
        setOtpDigits(next);
        if (cleaned && index < 5) { otpRefs.current[index + 1]?.focus(); }
    }

    function handleOtpKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    }

    function handleOtpPaste(e: React.ClipboardEvent) {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(NON_DIGIT_RE, '').slice(0, 6);
        if (pasted.length > 0) {
            const next = [...otpDigits];
            for (let i = 0; i < 6; i++) { next[i] = pasted[i] || ''; }
            setOtpDigits(next);
            const focusIndex = Math.min(pasted.length, 5);
            otpRefs.current[focusIndex]?.focus();
        }
    }

    async function handleOtpSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!otpState) { return; }
        setOtpLoading(true);
        setOtpError(null);
        try {
            const vs = await verifyOtp(csrfToken, email, otpCode, otpState);
            setVerifiedState(vs);
            const { status } = await checkApplicationStatus(csrfToken, email, applicationId, vs);
            switch (status) {
                case SubmissionStatus.Pending: setStep(Step.Pending); break;
                case SubmissionStatus.CanReapply: setStep(Step.CanReapply); break;
                case SubmissionStatus.ApprovedPendingActivation: setStep(Step.ApprovedPendingActivation); break;
                case SubmissionStatus.AlreadyActivated: setStep(Step.AlreadyActivated); break;
                default: setStep(Step.Details); break;
            }
        } catch (err) {
            setOtpError(err instanceof Error ? err.message : 'Invalid or expired code');
        } finally {
            setOtpLoading(false);
        }
    }

    async function handleDetailsSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!verifiedState) { return; }
        setSubmitting(true);
        setSubmitError(null);
        const result = await submitApplication(csrfToken, email, applicationId, form, verifiedState, isReapply);
        setSubmitting(false);
        if (!result.ok) {
            setSubmitError(result.error);
            return;
        }
        switch (result.status) {
            case SubmissionStatus.Submitted: setStep(Step.Submitted); break;
            case SubmissionStatus.Pending: setStep(Step.Pending); break;
            case SubmissionStatus.ApprovedPendingActivation: setStep(Step.ApprovedPendingActivation); break;
            case SubmissionStatus.AlreadyActivated: setStep(Step.AlreadyActivated); break;
            default: setStep(Step.Submitted); break;
        }
    }

    const logoHeader = (defaultImage || image) ? (
        <div className="logo-header">
            {defaultImage && <LogoImage image={defaultImage} alt="Faves" className="logo-img" sizes="200px" />}
            {defaultImage && image && <span className="logo-separator" />}
            {image && <LogoImage image={image} alt="Partner" className="logo-img" sizes="200px" />}
        </div>
    ) : null;

    function renderStep() {
        switch (step) {
            case Step.Email:
                return (
                    <div className="gform">
                        {logoHeader}
                        <p className="gform__eyebrow">Athlete Application</p>
                        <div className="form-header">
                            <span className="form-section-label">Step 1 of 3</span>
                            <span className="form-time">Confirm your email</span>
                        </div>
                        {emailError && <div className="gform__error">{emailError}</div>}
                        <form className="gform__fields" onSubmit={handleEmailSubmit}>
                            <div className="gform__field">
                                <label className="gform__label gform__label--required" htmlFor="email">Email Address</label>
                                <input className="gform__input" id="email" type="email" placeholder="you@example.com" required
                                    value={emailInput} onChange={e => setEmailInput(e.target.value)} />
                            </div>
                            <Button
                                label={emailLoading ? 'Sending code...' : 'Continue'}
                                type="submit"
                                variant="accent"
                                disabled={emailLoading}
                                className="apply__submit"
                            />
                        </form>
                    </div>
                );

            case Step.Otp:
                return (
                    <div className="gform">
                        {logoHeader}
                        <p className="gform__eyebrow">Athlete Application</p>
                        <div className="form-header">
                            <button type="button" className="back-btn" onClick={() => setStep(Step.Email)}>
                                <img src={arrowIcon} alt="" className="back-btn__icon" /> Back
                            </button>
                            <span className="form-section-label">Step 2 of 3</span>
                            <span className="form-time">Verify your email</span>
                        </div>
                        <p className="otp-hint">Code sent to <strong>{email}</strong>.</p>
                        {otpError && <div className="gform__error">{otpError}</div>}
                        <form className="gform__fields" onSubmit={handleOtpSubmit}>
                            <div className="gform__field otp-group">
                                <label className="gform__label gform__label--required">Enter 6-digit code</label>
                                <div className="otp-inputs" onPaste={handleOtpPaste}>
                                    {otpDigits.map((d, i) => (
                                        <input
                                            key={i}
                                            ref={el => { otpRefs.current[i] = el; }}
                                            className="otp-input"
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={1}
                                            value={d}
                                            onChange={e => handleOtpDigitChange(i, e.target.value)}
                                            onKeyDown={e => handleOtpKeyDown(i, e)}
                                            disabled={otpLoading}
                                        />
                                    ))}
                                </div>
                            </div>
                            <Button
                                label={otpLoading ? 'Verifying...' : 'Verify Code'}
                                type="submit"
                                variant="accent"
                                disabled={otpLoading || otpCode.length !== 6}
                                className="apply__submit"
                            />
                        </form>
                    </div>
                );

            case Step.Details: {
                if (!verifiedState) { return null; }
                const requiredExtraFieldsFilled = (formConfig?.extra_fields ?? [])
                    .filter(f => f.required)
                    .every(f => (form.extra_data[f.key] ?? []).length > 0);
                const canSubmit = !submitting && form.first_name && form.last_name && form.primary_sport && form.athlete_level && requiredExtraFieldsFilled;
                return (
                    <div className="gform">
                        {logoHeader}
                        <p className="gform__eyebrow">Athlete Application</p>
                        <div className="form-header">
                            <button type="button" className="back-btn" onClick={() => setStep(Step.Otp)}>
                                <img src={arrowIcon} alt="" className="back-btn__icon" /> Back
                            </button>
                            <span className="form-section-label">Step 3 of 3</span>
                            <span className="form-time">Your details</span>
                        </div>
                        {submitError && <div className="gform__error">{submitError}</div>}
                        <form className="gform__fields" onSubmit={handleDetailsSubmit}>
                            <div className="gform__row">
                                <div className="gform__field">
                                    <label className="gform__label gform__label--required" htmlFor="first_name">First Name</label>
                                    <input className="gform__input" id="first_name" type="text" placeholder="First name" required
                                        value={form.first_name} onChange={e => setField('first_name', e.target.value)} />
                                </div>
                                <div className="gform__field">
                                    <label className="gform__label gform__label--required" htmlFor="last_name">Last Name</label>
                                    <input className="gform__input" id="last_name" type="text" placeholder="Last name" required
                                        value={form.last_name} onChange={e => setField('last_name', e.target.value)} />
                                </div>
                            </div>
                            <div className="gform__row">
                                <div className="gform__field">
                                    <label className="gform__label" htmlFor="insta_handle">Instagram Handle</label>
                                    <input className="gform__input" id="insta_handle" type="text" placeholder="@handle"
                                        value={form.insta_handle} onChange={e => setField('insta_handle', e.target.value)} />
                                </div>
                                <div className="gform__field">
                                    <label className="gform__label" htmlFor="tiktok_handle">TikTok Handle</label>
                                    <input className="gform__input" id="tiktok_handle" type="text" placeholder="@handle"
                                        value={form.tiktok_handle} onChange={e => setField('tiktok_handle', e.target.value)} />
                                </div>
                            </div>
                            <div className="gform__field">
                                <label className="gform__label gform__label--required" htmlFor="primary_sport">Primary Sport</label>
                                <select className="gform__input" id="primary_sport" required
                                    value={form.primary_sport} onChange={e => setField('primary_sport', e.target.value)}>
                                    <option value="">Select sport</option>
                                    {supportedActivities.map(activity => <option key={activity.id} value={activity.label}>{activity.label}</option>)}
                                    <option value={OTHER_SPORT}>{OTHER_SPORT}</option>
                                </select>
                            </div>
                            <div className="gform__field">
                                <label className="gform__label gform__label--required" htmlFor="athlete_level">Level</label>
                                <select className="gform__input" id="athlete_level" required
                                    value={form.athlete_level} onChange={e => setField('athlete_level', e.target.value)}>
                                    <option value="">Select level</option>
                                    <option value="college">College</option>
                                    <option value="pro">Pro</option>
                                </select>
                            </div>
                            <div className="gform__row">
                                <div className="gform__field">
                                    <label className="gform__label" htmlFor="country">Country</label>
                                    <select className="gform__input" id="country"
                                        value={countryIso} onChange={e => handleCountryChange(e.target.value)}>
                                        <option value="">Select a country</option>
                                        {COUNTRY_OPTIONS.map(c => <option key={c.isoCode} value={c.isoCode}>{c.label}</option>)}
                                    </select>
                                </div>
                                <div className="gform__field">
                                    <label className="gform__label" htmlFor="state">State / Province</label>
                                    <select className="gform__input" id="state" disabled={!countryIso}
                                        value={stateIso} onChange={e => handleStateChange(e.target.value)}>
                                        <option value="">Select a state</option>
                                        {stateOptions.map(s => <option key={s.isoCode} value={s.isoCode}>{s.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="gform__field">
                                <label className="gform__label" htmlFor="city">City</label>
                                {cityOptions.length > 0 ? (
                                    <select className="gform__input" id="city" disabled={!stateIso}
                                        value={form.city} onChange={e => setField('city', e.target.value)}>
                                        <option value="">Select a city</option>
                                        {cityOptions.map(city => <option key={city} value={city}>{city}</option>)}
                                    </select>
                                ) : (
                                    <input className="gform__input" id="city" type="text" placeholder="Enter your city"
                                        disabled={!stateIso && !countryIso}
                                        value={form.city} onChange={e => setField('city', e.target.value)} />
                                )}
                            </div>
                            {!referral_code && (
                                <div className="gform__field">
                                    <label className="gform__label" htmlFor="heard_about_source">How did you hear about Faves?</label>
                                    <select className="gform__input" id="heard_about_source"
                                        value={form.heard_about_source} onChange={e => handleHeardAboutChange(e.target.value)}>
                                        <option value="">Select an option</option>
                                        {heardAboutOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                </div>
                            )}
                            {!referral_code && heardAboutFollowUp && (
                                <div className="gform__field">
                                    <label className="gform__label" htmlFor="referrer">{heardAboutFollowUp.label}</label>
                                    <input className="gform__input" id="referrer" type="text" maxLength={REFERRER_MAX_LENGTH}
                                        placeholder={heardAboutFollowUp.placeholder}
                                        value={form.referrer} onChange={e => setField('referrer', e.target.value)} />
                                </div>
                            )}
                            {(formConfig?.extra_fields ?? []).map(field => (
                                <div className="gform__field" key={field.key}>
                                    <label className={`gform__label${field.required ? ' gform__label--required' : ''}`} htmlFor={`extra-${field.key}`}>
                                        {field.label}
                                    </label>
                                    {field.type === 'multiselect' ? (
                                        <div className="checkbox-group" id={`extra-${field.key}`}>
                                            {(field.options ?? []).map(opt => (
                                                <label key={opt} className="checkbox-option">
                                                    <input type="checkbox"
                                                        checked={(form.extra_data[field.key] ?? []).includes(opt)}
                                                        onChange={() => toggleExtraFieldOption(field.key, opt)} />
                                                    {' '}{opt}
                                                </label>
                                            ))}
                                        </div>
                                    ) : field.type === 'select' ? (
                                        <select className="gform__input" id={`extra-${field.key}`} required={field.required}
                                            value={(form.extra_data[field.key] ?? [])[0] ?? ''}
                                            onChange={e => setExtraField(field.key, e.target.value ? [e.target.value] : [])}>
                                            <option value="">Select…</option>
                                            {(field.options ?? []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                        </select>
                                    ) : (
                                        <input className="gform__input" id={`extra-${field.key}`} type={field.type}
                                            placeholder={field.placeholder ?? ''} required={field.required}
                                            value={(form.extra_data[field.key] ?? [])[0] ?? ''}
                                            onChange={e => setExtraField(field.key, e.target.value ? [e.target.value] : [])} />
                                    )}
                                </div>
                            ))}
                            <Button
                                label={submitting ? 'Submitting...' : 'Submit Application'}
                                type="submit"
                                variant="accent"
                                disabled={!canSubmit}
                                className="apply__submit"
                            />
                            <p className="terms-blurb">
                                By submitting, you agree to our{' '}
                                <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                                {' '}and{' '}
                                <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                            </p>
                        </form>
                    </div>
                );
            }

            case Step.Submitted:
                return (
                    <div className="gform apply__notice">
                        <h2 className="step-title">Application Submitted!</h2>
                        <p className="step-desc">Thanks for applying. Our team will review your application and be in touch soon.</p>
                        <Button label="Return to faves" href="/" variant="accent" />
                    </div>
                );

            case Step.Pending:
                return (
                    <div className="gform apply__notice">
                        <h2 className="step-title">Application Under Review</h2>
                        <p className="step-desc">Your application is currently under review. We'll reach out when a decision has been made.</p>
                        <Button label="Return to faves" href="/" variant="accent" />
                    </div>
                );

            case Step.CanReapply:
                return (
                    <div className="gform apply__notice">
                        <h2 className="step-title">Application Under Review</h2>
                        <p className="step-desc">Your application is currently under review. We'll reach out when a decision has been made.</p>
                        <Button
                            label="Submit a new application"
                            variant="accent"
                            onClick={() => { setIsReapply(true); setStep(Step.Details); }}
                        />
                    </div>
                );

            case Step.ApprovedPendingActivation:
                return (
                    <div className="gform apply__notice">
                        <h2 className="step-title">Application Approved</h2>
                        <p className="step-desc">Your application has been approved! Check your email for an activation link to set your password and get started.</p>
                        <Button label="Return to faves" href="/" variant="accent" />
                    </div>
                );

            case Step.AlreadyActivated:
                return (
                    <div className="gform apply__notice">
                        <h2 className="step-title">Already Activated</h2>
                        <p className="step-desc">Your account has already been activated.</p>
                        <Button label="Log In" href="/portal/login" variant="accent" />
                    </div>
                );

            default:
                return null;
        }
    }

    const showError = applicationError;

    return (
        <div className="apply">
            <div className="apply__stage">
                <div className="apply__viewport">
                    {backgroundImage && <img className="apply__bg" src={backgroundImage} alt="" aria-hidden="true" />}
                    <SiteNav />
                    <div className="apply__content">
                        {showError ? (
                            <div className="gform apply__notice">
                                <h1 className="step-title">Application Not Found</h1>
                                <p className="step-desc">This application link is invalid or no longer available.</p>
                                <Button label="Return to faves" href="/" variant="accent" />
                            </div>
                        ) : (
                            <div className="apply__inner">
                                {/* Keyed by step so the card subtree remounts on
                                    Continue/Back, replaying the pop-in animation. */}
                                <React.Fragment key={step}>{renderStep()}</React.Fragment>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <SiteFooter csrfToken={csrfToken} />
        </div>
    );
}
